import * as Sentry from "@sentry/react";

import { auth } from "./firebase";
import { AxiosError } from "axios";
import { onAuthStateChanged } from "firebase/auth";

import {
  SENTRY_DSN,
  SENTRY_RELEASE,
  SENTRY_ENVIRONMENT,
  SENTRY_TRACES_SAMPLE_RATE,
} from "./constants/environment";

// Error monitoring with Sentry: crashes, unhandled errors and failed API
// requests (5xx, no answer) go to the Sentry project `website`. Without
// REACT_APP_SENTRY_DSN it's off: nothing is sent. See docs/deployment.md.
export function initSentry(): void {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    tracesSampleRate: tracesSampleRate(),
    // Nothing about the users: no headers, cookies, query strings (they hold
    // the sign-in and password reset tokens), bodies or local variables.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      stackFrameVariables: false,
    },
    // Sentry names a clicked or measured element by its `title`, `aria-label`
    // and `alt`, and those may hold data of users, e.g. the profile picture's
    // `alt`, the user's name. So no click breadcrumbs, no interaction (INP)
    // spans, and no elements in a page load's web vitals.
    integrations: [
      Sentry.browserTracingIntegration({ enableInp: false }),
      // What the console logs may hold data of users too.
      Sentry.breadcrumbsIntegration({ console: false, dom: false }),
    ],
    beforeSend: withoutQueries,
    beforeSendTransaction: (event) => withoutElements(withoutQueries(event)),
    beforeBreadcrumb: breadcrumbWithoutQueries,
  });

  // Names the signed-in user in what Sentry reports: by Firebase uid only.
  onAuthStateChanged(auth, (user) => {
    Sentry.setUser(user ? { id: user.uid } : null);
  });
}

// Axios' codes of a request that was stopped on purpose: cancelled, or
// aborted by the browser on a reload or a navigation (no timeout is set).
const STOPPED_CODES = ["ERR_CANCELED", "ECONNABORTED"];

// Reports a failed request to the API: one answered with a 5xx, or one
// without an answer (no connection), but not one that was stopped on purpose
// (STOPPED_CODES). Each request (method, route, status) is its own issue. An
// answer below 500 is the user's doing, not a failure. `thrown`, the error the
// caller gets, is reported, so it isn't reported again when it's left
// unhandled.
export function reportFailedRequest(error: AxiosError, thrown: Error): void {
  const status = error.response?.status;
  const failed =
    status === undefined
      ? !STOPPED_CODES.includes(error.code ?? "")
      : status >= 500;

  if (!failed) return;

  const method = (error.config?.method ?? "get").toLowerCase();
  const route = routeOf(error.config?.url);
  const answer = status === undefined ? "no answer" : String(status);

  Sentry.withScope((scope) => {
    scope.setFingerprint(["api", method, route, answer]);
    scope.setTag("api.status", answer);
    scope.setContext("api_request", { method, route, status });
    // Named by its request, rather than `Error`.
    scope.addEventProcessor((event) => {
      const values = event.exception?.values ?? [];
      const exception = values[values.length - 1];

      if (exception) {
        exception.type = `${method.toUpperCase()} ${route} (${answer})`;
      }

      return event;
    });

    Sentry.captureException(thrown);
  });
}

// A request's route, without host, query and ids, e.g. `/user/coupons/:id`.
function routeOf(url: string | undefined): string {
  if (!url) return "unknown";

  return new URL(url, window.location.origin).pathname.replace(
    /\/([0-9a-f]{24}|\d+)(?=\/|$)/gi,
    "/:id"
  );
}

// A URL without its query and fragment.
function withoutQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

// Drops the query of the page's URL from an event, its referrer, and the
// query of its stack frames' files: a frame of inline code, or of an error
// without a stack, is named by the page's URL.
function withoutQueries<T extends Sentry.Event>(event: T): T {
  if (event.request) {
    if (event.request.url) event.request.url = withoutQuery(event.request.url);

    delete event.request.query_string;
    delete event.request.headers?.Referer;
  }

  [...(event.exception?.values ?? []), ...(event.threads?.values ?? [])]
    .flatMap(({ stacktrace }) => stacktrace?.frames ?? [])
    .forEach((frame) => {
      if (frame.filename) frame.filename = withoutQuery(frame.filename);
      if (frame.abs_path) frame.abs_path = withoutQuery(frame.abs_path);
    });

  return event;
}

// Drops the elements a page load's web vitals name: the largest one
// (`lcp.element`) and those that shifted (`cls.source.<n>`).
function withoutElements<T extends Sentry.Event>(event: T): T {
  const data = event.contexts?.trace?.data;

  Object.keys(data ?? {})
    .filter((key) => key === "lcp.element" || key.startsWith("cls.source."))
    .forEach((key) => delete data?.[key]);

  return event;
}

// Drops the query of the URLs a breadcrumb names: of a request or a
// navigation.
function breadcrumbWithoutQueries(breadcrumb: Sentry.Breadcrumb) {
  const { data } = breadcrumb;

  if (data) {
    ["url", "from", "to"].forEach((field) => {
      if (typeof data[field] === "string") {
        data[field] = withoutQuery(data[field]);
      }
    });
  }

  return breadcrumb;
}

// How many page loads and navigations are traced, from 0 to 1. A value that
// isn't such a number traces none, rather than keep the site from starting.
function tracesSampleRate(): number {
  if (!SENTRY_TRACES_SAMPLE_RATE) return 0;

  const rate = Number(SENTRY_TRACES_SAMPLE_RATE);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    console.warn(
      "REACT_APP_SENTRY_TRACES_SAMPLE_RATE must be a number from 0 to 1"
    );
    return 0;
  }

  return rate;
}
